import {
  ErrorCodes,
  ExecuteParameters,
  ExecuteResponse,
  ExtensionLocator as ApiExtensionLocator,
  FilterableModel,
  Model,
  NotificationId,
  ParameterId as ApiParameterId,
  VerbId,
} from '@tableau/api-internal-contract-js';
import { PresentationLayer } from '@tableau/preslayer-api-contract';
import { NotificationId as PresLayerEventId } from '../gen-src/preslayer/Events';
import { ParameterId as PresLayerParameterId } from '../gen-src/preslayer/Params';
import { AddInInstancePresModel, AddInLocatorPresModel, PresentationModel } from '../gen-src/preslayer/PresModels';
import { ApiContext } from './ApiContext';
import { ApiUtils } from './common-bootstrap/ApiUtils';
import { Disposable } from './Disposable';
import { ErrorHelpers } from './ErrorHelpers';
import { InternalTableauError } from './InternalTableauError';
import { CommandLogging } from './logging/CommandLogging';
import { CommandMappingRegistry } from './mappings/CommandMappingRegistry';
import { ExtensionsCommandMappingRegistryFactory } from './mappings/ExtensionsCommandMappingRegistryFactory';
import { EventMappingRegistryFactory } from './mappings/ExtensionsEventMappingRegistryFactory';
import {
  ApiToPresLayerMappingRegistry,
  ParameterMappingRegistryFactory,
  PresLayerToApiMappingRegistry,
} from './mappings/ParameterMappingRegistryFactory';
import { EventMappingRegistry } from './mappings/SharedEventMappingRegistryFactory';
import { PermissionValidator } from './permissions/PermissionValidation';
import { telemetryFunctionComplete } from './telemetry/ExtensionsTelemetryDispatcher';
import { FunctionResults, telemetryFunctionResponse, telemetryFunctionStart } from './telemetry/TelemetryDispatcher';

export type NotificationHandler = (notification: NotificationId, model: Model) => void;

/**
 * Main class for the api-core project. This class is responsible for executing comamnd and marshalling notifications
 * between the internal Api and the pres-layer Api. After construction, initialize must be called in order to
 * use this class
 *
 * @export
 * @class ApiEventHandler
 */
export class ApiEventHandler implements Disposable {
  private _apiExtensionLocator?: ApiExtensionLocator;
  private _eventHandler?: NotificationHandler;
  private _commandRegistry?: CommandMappingRegistry;
  private _eventRegistry?: EventMappingRegistry;
  private _apiToPresLayerRegistry?: ApiToPresLayerMappingRegistry;
  private _presLayerToApiRegistry?: PresLayerToApiMappingRegistry;
  private _permissionValidator?: PermissionValidator;
  private _presLayerAddInLocator: AddInLocatorPresModel;
  private _isInitialized = false;
  private _notificationUnregisterFunctions: Array<() => void> = [];

  /**
   * Creates an instance of ApiEventHandler.
   * @param {PresentationLayer} presLayer - An implementation of the pres-layer Api to use
   * @param {AddInLocatorPresModel} addInLocatorPresModel - locator for the add-in which is being referenced
   * @param {runningInDesktop} boolean - true if this is ApiEventHandler is created in a Desktop context.
   * @memberof ApiEventHandler
   */
  public constructor(private _presLayer: PresentationLayer, private _presLayerExtensionInstance: AddInInstancePresModel) {
    this._presLayerAddInLocator = this._presLayerExtensionInstance.addInLocatorPresModel;
  }

  /**
   * Initializes the ApiEventsHandler and its registries. Must be called before this class is used
   */
  public initialize(): void {
    // Initialize all our registries
    this._commandRegistry = ExtensionsCommandMappingRegistryFactory.createCommandMappingRegistry();
    this._apiToPresLayerRegistry = ParameterMappingRegistryFactory.createApiToPresLayerParamRegistry();
    this._presLayerToApiRegistry = ParameterMappingRegistryFactory.createPresLayerToApiParamRegistry();
    this._eventRegistry = EventMappingRegistryFactory.createEventMappingRegistry();

    // Initialize Permission Validation
    const extensionName = this._presLayerExtensionInstance.addInRegistrationPresModel.addInName;
    const extensionId = this._presLayerExtensionInstance.addInRegistrationPresModel.addInId;
    const capabilities = this._presLayerExtensionInstance.addInRegistrationPresModel.extensionPermissions;
    this._permissionValidator = new PermissionValidator(extensionName, extensionId, capabilities);

    // Convert our addInLocator to the Api version
    this._apiExtensionLocator = this._presLayerToApiRegistry.get(
      PresLayerParameterId.AddInLocator,
      ApiParameterId.ExtensionLocator,
    )(this._presLayerAddInLocator) as ApiExtensionLocator;

    for (const plEventId of Object.keys(this._eventRegistry)) {
      // Register for all notifications we know about and bind the event type to the callback
      this._notificationUnregisterFunctions.push(
        this._presLayer.registerNotificationHandler(plEventId, this.onPresLayerNotification.bind(this, plEventId)),
      );
    }

    this._isInitialized = true;
  }

  /**
   * Sets an event handler function to be notified when Api events come in. Only a single registration is supported.
   *
   * @param {NotificationHandler} eventHandler
   * @memberof ApiEventHandler
   */
  public setEventHandler(eventHandler: NotificationHandler): void {
    this._eventHandler = eventHandler;
  }

  /**
   * Executes the requested Api command by converting the parameters, running the command against the pres-layer,
   * then processing the result back into a format the Api can understand
   *
   * @param {VerbId} verb
   * @param {ExecuteParameters} parameters
   * @returns {Promise<ExecuteResponse>}
   * @memberof ApiEventHandler
   */
  public execute(verb: VerbId, parameters: ExecuteParameters): Promise<ExecuteResponse> {
    this.assertInitialized();
    this.assertVisible();

    const command = this._commandRegistry!.getCommand(verb);
    if (!this._permissionValidator!.validate(command.commandId)) {
      return Promise.reject(ErrorHelpers.permissionDeniedError(command.commandId));
    }
    parameters = parameters || {};

    // Augment the command with the locator if it's needed
    if (command.needsAddInLocator) {
      parameters[ApiParameterId.ExtensionLocator] = this._apiExtensionLocator!;
    }

    const commandsParams = command.createParams(parameters, this._apiToPresLayerRegistry!);

    // Add a param to indicate that the command is executed by this extension
    commandsParams[PresLayerParameterId.CommandsController_CommandLoggingParams] = CommandLogging.buildLoggingParamsPresModel(
      this._presLayerExtensionInstance,
      command.commandId,
    );

    const runtimeData = telemetryFunctionStart();

    return this._presLayer
      .invokeCommand<ApiUtils.CommandResult>(command.namespace, command.commandId, commandsParams)
      .then<ExecuteResponse>((commandResult) => {
        telemetryFunctionResponse(runtimeData);

        const convertedResult = command.ProcessResult(commandResult, this._presLayerToApiRegistry!);

        telemetryFunctionComplete(
          verb,
          runtimeData,
          parameters,
          FunctionResults.Success(convertedResult),
          this._presLayerExtensionInstance,
        );

        return {
          result: convertedResult,
        };
      });
  }

  public get ApiExtensionLocator(): ApiExtensionLocator {
    return this._apiExtensionLocator!;
  }

  public dispose(): void {
    this.unregisterNotificationHandlers();
  }

  private unregisterNotificationHandlers(): void {
    for (const unregisterNotification of this._notificationUnregisterFunctions) {
      unregisterNotification();
    }
  }

  /**
   * Helper method to check that things are initialized as expected. Throws if initialization not complete
   *
   * @private
   * @memberof ApiEventHandler
   */
  private assertInitialized(): void {
    if (!this._isInitialized) {
      throw new InternalTableauError(ErrorCodes.VERSION_NOT_CONFIGURED, 'Cannot run commands, API is not initialized.');
    }
  }

  /**
   * Helper method to check that the extension is visible. Throws if things aren't visible.
   *
   * @private
   * @memberof ApiEventHandler
   */
  private assertVisible(): void {
    const apiContext = ApiContext.getInstance();
    // Visibility error is temporarily disabled for https://tfs.tsi.lan/tfs/DefaultCollection/Tableau-Dev/_workitems/edit/862942,
    //  will be turned on again with https://tfs.tsi.lan/tfs/DefaultCollection/Tableau-Dev/_workitems/edit/891590
    const temporarilyDisabled = true;
    if (!temporarilyDisabled && !apiContext.isVisible()) {
      throw new InternalTableauError(ErrorCodes.VISIBILITY_ERROR, 'Cannot run commands, extension is currently not in the foreground.');
    }
  }

  /**
   * Called when a new event notification comes in from the presentation layer
   *
   * @param eventId The pres layer event id
   * @param presModel The pres model that is included with the event
   */
  private onPresLayerNotification(eventId: PresLayerEventId, presModel: PresentationModel): void {
    // First look up this eventId in our registry and convert to presModel
    const eventMapping = this._eventRegistry![eventId];
    const apiModel = eventMapping.converter(presModel) as FilterableModel;

    if (this._eventHandler) {
      if (!apiModel.filterByLocator) {
        this._eventHandler(eventMapping.apiId, apiModel);
      } else {
        // All events from the platform come through this code path, but certain events should
        // only be propagated upward to clients if they are relevant to the extension. For example,
        // a settings event should only be sent to the specific extension which settings have changed.
        if (this._locatorsRepresentSameExtensionInstance(apiModel.locator, this._apiExtensionLocator!)) {
          this._eventHandler(eventMapping.apiId, apiModel);
        }
      }
    }
  }

  /**
   * Returns true if two locators represent the same extension instance.  Each extension instance
   * has a unique instance ID, so that is the primary criterion.  However, there is an edge case where two extension
   * instances can share the same instance ID, but represent different extensions.  This only occurs when
   * an extension is running inside a dashboard inside a story.  The story extension is treated as a
   * separate instance of the extension than the dashboard extension, even while sharing an instance ID.
   *
   * @param locatorA first locator to compare
   * @param locatorB second locator to compare
   */
  private _locatorsRepresentSameExtensionInstance(locatorA: ApiExtensionLocator, locatorB: ApiExtensionLocator): boolean {
    if (locatorA.dashboardPath && locatorB.dashboardPath) {
      return locatorA.instanceId === locatorB.instanceId && locatorA.dashboardPath.isDashboard === locatorB.dashboardPath.isDashboard;
    } else {
      return false;
    }
  }
}
